import { z } from 'zod';
import { EdiType, EntryType, SchemaSource, SchemaVariant } from '../schemas';

const recentFileSchema = z.object({
  fileId: z.string(),
  fileName: z.string(),
  // Accept ISO string or Date from client and coerce to Date
  usedAt: z.coerce.date(),
  mimeType: z.string(),
  userId: z.string(),
});

export const templateRefSchema = z.object({
  id: z.string(),
  operatorId: z.number(),
  name: z.string(),
  path: z.string().nullable(),
  deployPath: z.string().nullable(),
  // TODO: where place domain
  // TODO: should be enum
  templatetype: z.string(),
  domain: z.string(),
  categoryId: z.string(),
  priority: z.number(),
  inputsource: z.nativeEnum(SchemaSource),
  description: z.string().optional(),
  enabled: z.boolean(),
  deleted: z.boolean().optional(),
  isStatic: z.boolean(),
  ediType: z.nativeEnum(EdiType).optional(),
  ediVariables: z
    .record(
      z.string(),
      z.object({
        value: z.union([z.string(), z.number()]),
        schemaVariable: z.string(),
      }),
    )
    .optional(),
  recentFiles: z.array(recentFileSchema).optional(),
});

export type TemplateRef = z.infer<typeof templateRefSchema>;

// TODO: reduce duplication?
// TODO: stronger typing
export const templateSaveSchema = z.object({
  id: z.string(),
  operatorId: z.number(),
  name: z.string(),
  domain: z.string(),
  type: z.string(),
  variant: z.nativeEnum(SchemaVariant),
  schemaId: z.string(),
  entries: z.record(z.string(), z.any()),
  graphs: z.record(z.string(), z.any()),
  suggestions: z
    .record(
      z.string(),
      z.array(
        z
          .object({
            value: z.any(),
            type: z.nativeEnum(EntryType),
            snippet: z.any(),
            autofill: z.boolean().optional(),
            source: z.string().optional(),
            applied: z.boolean(),
          })
          .passthrough(),
      ),
    )
    .optional(),
  config: z.object({
    preconfiguration: z.any(),
    configSnapshot: z.any().optional(),
    updateDiscardIds: z.array(z.string()),
    lastTextContentHash: z.string().nullable(),
  }),
  plugins: z.object({
    global: z.object({
      plugins: z.array(z.any()),
      versions: z.record(z.string(), z.string()),
      bootstrapped: z.record(z.string(), z.boolean()),
    }),
    registered: z.record(
      z.string(),
      z.object({
        version: z.string(),
        slice: z.any(),
      }),
    ),
  }),
});

export type TemplateSave = z.infer<typeof templateSaveSchema>;

export const templateResourceSchema = templateRefSchema
  .merge(
    z.object({
      save: templateSaveSchema,
    }),
  )
  .omit({ path: true });

export type TemplateResource = z.infer<typeof templateResourceSchema>;

export const createTemplateResourceDtoSchema = templateResourceSchema
  .extend({
    save: z.string(),
  })
  .omit({ id: true });

export type CreateTemplateResourceDto = z.infer<
  typeof createTemplateResourceDtoSchema
>;

// Make everything optional
export const updateTemplateResourceDtoSchema = templateResourceSchema
  .extend({
    save: z.string().optional(),
  })
  .omit({ id: true })
  .partial();

export type UpdateTemplateResourceDto = z.infer<
  typeof updateTemplateResourceDtoSchema
>;

export const templateDeploySchema = z.object({
  id: z.string(),
  operatorId: z.number(),
  name: z.string(),
  domain: z.string(),
  type: z.string(),
  variant: z.string(),
  file: z.string(),
});

export type TemplateDeploy = z.infer<typeof templateDeploySchema>;

export const templateCategorySchema = z.object({
  id: z.string(),
  operatorId: z.number(),
  domain: z.string(),
  name: z.string(),
});

export type TemplateCategory = z.infer<typeof templateCategorySchema>;

export const rawTemplateDeploySchema = z.object({
  deployPath: z.string(),
  content: z.string(),
});

export type RawTemplateDeploy = z.infer<typeof rawTemplateDeploySchema>;
